<div class="flex flex-col p-5 w-full">
    <div class="flex justify-between pb-2 items-center">
        <div class="pl-2 header-title">Flows</div>
        @if ((flowsEditFeature$ | async)?.state === FeatureState.ENABLED) {
            <button color="primary" mat-stroked-button (click)="createFlow()">Create Flow</button>
        }
    </div>
    <div class="bg-white rounded-xl shadow-sm overflow-hidden">
        <table mat-table [dataSource]="(flows$ | async) ?? []">
            <ng-container matColumnDef="name">
                <th mat-header-cell *matHeaderCellDef>Name</th>
                <td mat-cell *matCellDef="let flow">
                    <div>
                        {{ flow.name }}
                    </div>
                </td>
            </ng-container>

            <ng-container matColumnDef="version">
                <th mat-header-cell *matHeaderCellDef>Version</th>
                <td mat-cell *matCellDef="let flow">
                    <div>{{ flow.version }}</div>
                </td>
            </ng-container>

            <ng-container matColumnDef="last-modified">
                <th mat-header-cell *matHeaderCellDef>Last Modified</th>
                <td mat-cell *matCellDef="let flow">
                    <div>{{ flow.lastModified | date: 'yyyy-MM-dd HH:mm' }}</div>
                    <div class="text-sm text-dark-500">{{ flow.lastModified | timeSinceNow }}</div>
                </td>
            </ng-container>

            <ng-container matColumnDef="devices">
                <th mat-header-cell *matHeaderCellDef>Devices</th>
                <td mat-cell *matCellDef="let flow">
                    <div class="flex gap-2 items-center">
                        {{ flow.devices.length }}

                        @if (olderDevicesCount(flow.devices, flow.version) > 0) {
                            <myrtls-icon
                                class="text-warning-600"
                                icon="warning"
                                [matTooltip]="
                                    'Some of the devices are not using the latest version of the flow (' +
                                    olderDevicesCount(flow.devices, flow.version) +
                                    ' out of ' +
                                    flow.devices.length +
                                    ').'
                                "
                            ></myrtls-icon>
                        }
                    </div>
                </td>
            </ng-container>

            <ng-container matColumnDef="actions">
                <th mat-header-cell *matHeaderCellDef>
                    <div class="pl-2">Actions</div>
                </th>
                <td mat-cell *matCellDef="let flow" class="w-[400px]">
                    <div class="flex gap-2">
                        <button class="text-brand" mat-button (click)="sendFlow(flow.id)">Send to Device</button>
                        <button mat-button (click)="downloadFlow(flow)">Download</button>
                        @if ((flowsEditFeature$ | async)?.state === FeatureState.ENABLED) {
                            <button mat-button (click)="editFlow(flow)">Edit</button>
                            <button class="text-error-800" mat-button (click)="deleteFlow(flow.id)">Delete</button>
                        }
                    </div>
                </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="displayedColumns" class="font-semibold"></tr>
            <tr mat-row *matRowDef="let user; columns: displayedColumns"></tr>

            <tr class="mat-row" *matNoDataRow>
                <td class="mat-cell" colspan="5">
                    @if (flowsLoading$ | async) {
                        <div class="w-full flex items-center justify-center p-8">
                            <mat-spinner [diameter]="40"></mat-spinner>
                        </div>
                    } @else {
                        <div class="p-6 text-dark-500">No flows found.</div>
                    }
                </td>
            </tr>
        </table>
    </div>

    <div class="flex justify-between pb-2 items-center mt-10">
        <div class="pl-2 header-title">Devices</div>
    </div>
    <div class="bg-white rounded-xl shadow-sm overflow-hidden">
        <table mat-table [dataSource]="(devices$ | async) ?? []">
            <ng-container matColumnDef="id">
                <th mat-header-cell *matHeaderCellDef>ID</th>
                <td mat-cell *matCellDef="let device">
                    <div [matTooltip]="device.id">{{ device.id | truncateMiddle }}</div>
                </td>
            </ng-container>

            <ng-container matColumnDef="device-information">
                <th mat-header-cell *matHeaderCellDef>Device Information</th>
                <td mat-cell *matCellDef="let device">
                    <div>
                        <div>{{ device.model || '-' }}</div>
                        <div class="text-sm text-dark-500">
                            {{ formatOperatingSystem(device.operatingSystem) || '-' }} {{ device.osVersion || '-' }}
                        </div>
                    </div>
                </td>
            </ng-container>

            <ng-container matColumnDef="flow-name">
                <th mat-header-cell *matHeaderCellDef>Flow Name</th>
                <td mat-cell *matCellDef="let device">
                    <div>
                        {{ device.flowName }}
                    </div>
                </td>
            </ng-container>

            <ng-container matColumnDef="flow-version">
                <th mat-header-cell *matHeaderCellDef>Flow Version</th>
                <td mat-cell *matCellDef="let device">
                    <div class="flex gap-2 items-center">
                        {{ device.flowVersion }}
                        @if (device.flowVersion < device.latestFlowVersion) {
                            <myrtls-icon
                                matTooltip="Device is using an older version of the flow."
                                class="text-warning-600"
                                icon="warning"
                            ></myrtls-icon>
                        }
                    </div>
                </td>
            </ng-container>

            <ng-container matColumnDef="note">
                <th mat-header-cell *matHeaderCellDef>Note</th>
                <td mat-cell *matCellDef="let device">
                    <span class="truncate max-w-[200px] text-sm" [class.text-dark-400]="!device.note">{{ device.note || '—' }}</span>
                </td>
            </ng-container>

            <ng-container matColumnDef="device-actions">
                <th mat-header-cell *matHeaderCellDef>
                    <div class="pl-2">Actions</div>
                </th>
                <td mat-cell *matCellDef="let device">
                    <div class="flex gap-2">
                        <button mat-button (click)="editNote(device)">Edit</button>
                        <button class="text-error-800" mat-button (click)="deleteDevice(device.id)">Delete</button>
                    </div>
                </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="displayedDeviceColumns" class="font-semibold"></tr>
            <tr mat-row *matRowDef="let row; columns: displayedDeviceColumns"></tr>

            <tr class="mat-row" *matNoDataRow>
                <td class="mat-cell" colspan="6">
                    @if (flowsLoading$ | async) {
                        <div class="w-full flex items-center justify-center p-8">
                            <mat-spinner [diameter]="40"></mat-spinner>
                        </div>
                    } @else {
                        <div class="p-6 text-dark-500">No devices found.</div>
                    }
                </td>
            </tr>
        </table>
    </div>
</div>
